import { createSlice } from "@reduxjs/toolkit";
import { LoadState } from "../../constants/enums";
import {
  IMirtapbxCallLogState,
  IMirtapbxCallLogFilters,
} from "./mirtapbxCallLog.types";
import {
  fetchCallLogsAsync,
  fetchUnlinkedCallsAsync,
  fetchUnlinkedCountAsync,
  fetchRecordingUrlAsync,
  fetchCallAiAsync,
  manualLinkCallAsync,
} from "./mirtapbxCallLogThunk";

export const initialMirtapbxCallLogState: IMirtapbxCallLogState = {
  list: [],
  total: 0,
  loading: LoadState.NotLoaded,
  filters: {},
  pageNo: 1,
  itemPerPage: 25,
  unlinked: {
    list: [],
    total: 0,
    loading: LoadState.NotLoaded,
    count: 0,
  },
  recordingUrl: {},
  ai: {},
  manualLinkLoading: LoadState.NotLoaded,
};

const mirtapbxCallLogSlice = createSlice({
  name: "mirtapbxCallLog",
  initialState: initialMirtapbxCallLogState,
  reducers: {
    setCallLogFilters: (
      state,
      action: { payload: IMirtapbxCallLogFilters },
    ) => {
      state.filters = action.payload;
    },
    setCallLogPage: (
      state,
      action: { payload: { pageNo?: number; itemPerPage?: number } },
    ) => {
      if (action.payload.pageNo != null) state.pageNo = action.payload.pageNo;
      if (action.payload.itemPerPage != null) state.itemPerPage = action.payload.itemPerPage;
    },
    clearCallLogs: (state) => {
      state.list = [];
      state.total = 0;
      state.loading = LoadState.NotLoaded;
    },
    clearUnlinkedCalls: (state) => {
      state.unlinked = { ...initialMirtapbxCallLogState.unlinked };
    },
    clearRecordingUrl: (state, action: { payload: number }) => {
      delete state.recordingUrl[action.payload];
    },
  },
  extraReducers: (builder) => {
    // Call logs list (active / scoped)
    builder.addCase(fetchCallLogsAsync.pending, (state) => {
      state.loading = LoadState.InProgress;
    });
    builder.addCase(fetchCallLogsAsync.fulfilled, (state, action) => {
      state.list = action.payload.data;
      state.total = action.payload.total;
      state.loading = LoadState.Loaded;
    });
    builder.addCase(fetchCallLogsAsync.rejected, (state) => {
      state.loading = LoadState.Failed;
      state.list = [];
      state.total = 0;
    });

    // Unlinked calls list (feeds unlinked.list + unlinked.count)
    builder.addCase(fetchUnlinkedCallsAsync.pending, (state) => {
      state.unlinked.loading = LoadState.InProgress;
    });
    builder.addCase(fetchUnlinkedCallsAsync.fulfilled, (state, action) => {
      state.unlinked.list = action.payload.data;
      state.unlinked.total = action.payload.total;
      state.unlinked.count = action.payload.total;
      state.unlinked.loading = LoadState.Loaded;
    });
    builder.addCase(fetchUnlinkedCallsAsync.rejected, (state) => {
      state.unlinked.loading = LoadState.Failed;
    });

    // Count-only poll (header badge). Updates ONLY the count so the badge's
    // minimal-page poll can never overwrite the inbox page's full list.
    builder.addCase(fetchUnlinkedCountAsync.fulfilled, (state, action) => {
      state.unlinked.count = action.payload.total;
    });

    // Signed recording URL, keyed by mirtapbx_cdr_id (never cached past TTL)
    builder.addCase(fetchRecordingUrlAsync.pending, (state, action) => {
      const id = action.meta.arg.id;
      state.recordingUrl[id] = {
        url: state.recordingUrl[id]?.url ?? null,
        expiresAt: state.recordingUrl[id]?.expiresAt ?? null,
        loading: LoadState.InProgress,
      };
    });
    builder.addCase(fetchRecordingUrlAsync.fulfilled, (state, action) => {
      state.recordingUrl[action.payload.id] = {
        url: action.payload.signedUrl,
        expiresAt: action.payload.expiresAt,
        loading: LoadState.Loaded,
      };
    });
    builder.addCase(fetchRecordingUrlAsync.rejected, (state, action) => {
      const id = action.meta.arg.id;
      state.recordingUrl[id] = {
        url: null,
        expiresAt: null,
        loading: LoadState.Failed,
      };
    });

    // AI analysis, keyed by mirtapbx_cdr_id (null = not ready)
    builder.addCase(fetchCallAiAsync.pending, (state, action) => {
      const id = action.meta.arg.id;
      state.ai[id] = {
        data: state.ai[id]?.data ?? null,
        loading: LoadState.InProgress,
      };
    });
    builder.addCase(fetchCallAiAsync.fulfilled, (state, action) => {
      state.ai[action.payload.id] = {
        data: action.payload.data,
        loading: LoadState.Loaded,
      };
    });
    builder.addCase(fetchCallAiAsync.rejected, (state, action) => {
      const id = action.meta.arg.id;
      state.ai[id] = {
        data: null,
        loading: LoadState.Failed,
      };
    });

    // Manual link
    builder.addCase(manualLinkCallAsync.pending, (state) => {
      state.manualLinkLoading = LoadState.InProgress;
    });
    builder.addCase(manualLinkCallAsync.fulfilled, (state) => {
      state.manualLinkLoading = LoadState.Loaded;
    });
    builder.addCase(manualLinkCallAsync.rejected, (state) => {
      state.manualLinkLoading = LoadState.Failed;
    });
  },
});

export const {
  setCallLogFilters,
  setCallLogPage,
  clearCallLogs,
  clearUnlinkedCalls,
  clearRecordingUrl,
} = mirtapbxCallLogSlice.actions;

export const mirtapbxCallLogReducer = mirtapbxCallLogSlice.reducer;
